import { coerceBooleanProperty } from "@angular/cdk/coercion";
import {
    ChangeDetectorRef, DestroyRef, Directive, ElementRef, inject, Input, OnChanges, OnInit, SimpleChanges, Type
} from "@angular/core";
import {
    MatButton, MatFabButton, MatIconButton, MatMiniFabButton
} from "@angular/material/button";
import { MatCheckbox } from "@angular/material/checkbox";
import { MatSlideToggle } from "@angular/material/slide-toggle";
import { MatSlider } from "@angular/material/slider";
import { MatSelect } from "@angular/material/select";
import { MatInput } from "@angular/material/input";
import { MatRadioButton } from "@angular/material/radio";
import { MatChipInput } from "@angular/material/chips";
import { MatDatepickerInput } from "@angular/material/datepicker";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { MatPaginator } from "@angular/material/paginator";
import { COMMON_APP_SERVICE } from "../../constants/app-tokens";
import {
    MatListItem, MatNavList
} from "@angular/material/list";
import { MatSort } from "@angular/material/sort";
import { MatMenuItem } from "@angular/material/menu";
import { DisabledDirective } from "../disabled/disabled.directive";

/**
 * Interface for elements that can be disabled.
 * Used to type-check components that have a disabled property.
 */
interface IDisableElement {
    disabled?: boolean;
}

/**
 * Directive that automatically disables Angular Material components during application processing states.
 *
 * This directive can be applied to any Material component that supports the disabled property.
 * It will automatically disable the component when the application is in a processing state,
 * and restore its previous disabled state when processing completes.
 *
 * Usage:
 * ```html
 * <button mat-button mgdProcessing>Click me</button>
 * <mat-checkbox mgdProcessing [disableWhileProcessing]="false">Check me</mat-checkbox>
 * ```
 */
@Directive({
    selector: "[mgdProcessing]",
})
export class ProcessingDirective implements OnInit, OnChanges {

    /**
     * When this is true, the element will be disabled during processing
     *
     * Default is true
     */
    @Input() disableWhileProcessing?: boolean = true;

    /**
     * Allows the host to pass a disabled binding that will be respected
     * as a fallback when the app is not processing.
     *
     * Default is false
     */
    @Input({
        alias: "disabled",
        transform: coerceBooleanProperty
    }) disabledInput?: boolean = false;

    /**
     * Returns true if the element is currently disabled
     */
    get disabled(): boolean {
        return (this._selfDisabled || this._element?.disabled) ?? false;
    }

    /**
     * This is set by the host directive/component using the setHostDisabled function
     */
    private _selfDisabled = false;

    /**
     * List of Material components that can be disabled.
     * Used to inject and control the disabled state of supported components.
     */
    private readonly _disableInjectors: Array<Type<IDisableElement>> = [
        MatButton,
        MatIconButton,
        MatFabButton,
        MatMiniFabButton,
        MatCheckbox,
        MatSlideToggle,
        MatSlider,
        MatSelect,
        MatInput,
        MatRadioButton,
        MatChipInput,
        MatDatepickerInput,
        MatPaginator,
        MatListItem,
        MatNavList,
        MatSort,
        MatMenuItem,
        DisabledDirective
    ];

    /**
     * Reference to the element that will be disabled
     */
    private readonly _element: IDisableElement;

    /**
     * Application state service that provides the processing state
     */
    private readonly _appState = inject(COMMON_APP_SERVICE);

    /**
     * Reference used to clean up subscriptions when the directive is destroyed
     */
    private readonly _destroyRef = inject(DestroyRef);

    /**
     * Reference to the ChangeDetectorRef service
     */
    private readonly _changeDetector = inject(ChangeDetectorRef);

    constructor() {

        for (const injector of this._disableInjectors) {
            const element = inject(injector, {
                optional: true,
                self: true
            });
            if (element) {
                this._element = element;
                break;
            }
        }

        if (!this._element) {
            const elementRef = inject(ElementRef);
            if (elementRef.nativeElement && "disabled" in elementRef.nativeElement) {
                this._element = elementRef.nativeElement;
            }
        }
    }

    /**
     * Subscribe to the application processing state and update disabled state accordingly
     */
    ngOnInit(): void {
        this._appState.processing$.pipe(
            // Unsubscribe when the component is destroyed
            takeUntilDestroyed(this._destroyRef)
        ).subscribe(() => this._setDisabled());
    }

    /**
     * Handle changes to inputs, particularly disableWhileProcessing
     * @param changes SimpleChanges object containing changed properties
     */
    ngOnChanges(changes: SimpleChanges): void {
        if (changes.disableWhileProcessing && this._appState.processing) {
            this._setDisabled();
        }
    }

    /**
     * Used to set disabled programmatically from a Directive or Component using this as a host
     *
     * For example if you have a ActionButtonDirective,
     * When there are no actions it setSelfDisabled to true and this element will become disabled
     * When there are actions it calls setSelfDisable to false and this element's disabled state will fall-back
     * to the processing state
     *
     */
    setSelfDisabled(value: boolean): void {
        this._selfDisabled = value;
        this._setDisabled();
    }

    /**
     * Sets the disabled state of the element based on application processing state
     * and directive inputs
     */
    private _setDisabled(): void {
        let disabled: boolean;
        if (this._appState.processing && (this.disableWhileProcessing ?? true)) {
            disabled = true;
        }
        else {
            disabled = this._selfDisabled || this.disabledInput;
        }
        if (this._element) {
            this._element.disabled = disabled;
        }
        this._changeDetector.markForCheck();
    }
}
